// The conversation behind a dashboard's Questions panel. Each question goes to
// dashaiChat in dashboard mode, which checks the asker's per-country scope on
// that dashboard and answers from its data. The first question creates a chat
// tagged `dashboard_panel`, which keeps it out of the DashAI history.

import { useCallback, useEffect, useRef, useState } from "react";

import { readProblem } from "@/api/problem";
import { getChatUrl, postWithAuthRetry } from "../lib/chatClient";
import { chatStoreClient } from "../lib/chatStoreClient";
import { readSseFrames } from "../lib/spikeDropClient";
import type { ChartData } from "../types/dashai";

export type DashboardPanelKey = "execution_view" | "key_accounts";

export interface PanelMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  chart?: ChartData;
  /** Follow-up questions the server offered with this answer. */
  suggestions?: string[];
  /** The answer's id in the chat store, which a thumbs vote is recorded against. */
  storedId?: string;
}

/** What has arrived of an answer so far. */
type AnswerSoFar = Pick<PanelMessage, "content" | "chart" | "suggestions">;

/** Why the last question got no answer. */
export type PanelFailure = "denied" | "blocked" | "failed";

export interface DashboardPanelChatInput {
  dashboard: DashboardPanelKey;
  /** The page's current view, such as a Key Accounts tab, or null when it has one view. */
  view: string | null;
  userId: string;
  idClient: string;
  country: string;
  filters: Record<string, string>;
}

type ChatFrame =
  | { type: "answer"; content?: string }
  | { type: "chart"; data?: ChartData }
  | { type: "suggestions"; suggestions?: string[] }
  | { type: "error"; message?: string }
  | { type: "done"; message_id?: string }
  | { type: string };

type AnswerOutcome = { outcome: "done"; storedId?: string } | { outcome: "failed" };

const TITLE_LENGTH = 80;

/**
 * Reads dashaiChat's answer stream, passing the answer so far to `onUpdate` as
 * it grows. Resolves `done` with the stored answer's id once the stream ends
 * cleanly with an answer, and `failed` on an error frame, a cut stream, or a
 * stream with no answer.
 */
async function readAnswer(
  res: Response,
  onUpdate: (answer: AnswerSoFar) => void
): Promise<AnswerOutcome> {
  const reader = res.body?.getReader();
  if (!reader) return { outcome: "failed" };
  const decoder = new TextDecoder();
  let buffer = "";
  const answer: AnswerSoFar = { content: "" };

  for (;;) {
    const { value, done } = await reader.read();
    if (value) buffer += decoder.decode(value, { stream: !done });
    const { events, rest, truncated } = readSseFrames<ChatFrame>(buffer, done);
    buffer = rest;
    for (const event of events) {
      if (event.type === "answer") {
        answer.content += (event as { content?: string }).content ?? "";
        onUpdate({ ...answer });
      } else if (event.type === "chart") {
        const chart = (event as { data?: ChartData }).data;
        if (chart) answer.chart = chart;
        if (answer.content) onUpdate({ ...answer });
      } else if (event.type === "suggestions") {
        const suggestions = (event as { suggestions?: string[] }).suggestions;
        if (suggestions?.length) answer.suggestions = suggestions;
        if (answer.content) onUpdate({ ...answer });
      } else if (event.type === "error") {
        return { outcome: "failed" };
      } else if (event.type === "done") {
        if (!answer.content) return { outcome: "failed" };
        return { outcome: "done", storedId: (event as { message_id?: string }).message_id };
      }
    }
    if (truncated || done) return { outcome: "failed" };
  }
}

export function useDashboardPanelChat({
  dashboard,
  view,
  userId,
  idClient,
  country,
  filters,
}: DashboardPanelChatInput) {
  const [messages, setMessages] = useState<PanelMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // A blocked failure carries the server's reason for refusing the view.
  const [failure, setFailure] = useState<{ kind: PanelFailure; reason: string | null } | null>(
    null
  );
  const [failedQuestion, setFailedQuestion] = useState<string | null>(null);

  // The ref guards the create-once flow inside `ask`; the state lets the panel
  // render controls that need the chat, such as thumbs votes.
  const chatIdRef = useRef<string | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsLoading(false);
  }, []);

  // A conversation is about one dashboard in one workspace.
  useEffect(() => {
    cancel();
    chatIdRef.current = null;
    setChatId(null);
    setMessages([]);
    setFailure(null);
    setFailedQuestion(null);
  }, [dashboard, idClient, cancel]);

  // A refusal or failure belongs to the view and market it was asked from. The
  // failed question stays in the box, so the user can send it from the new one.
  useEffect(() => {
    setFailure(null);
  }, [view, country]);

  useEffect(() => cancel, [cancel]);

  const ask = useCallback(
    async (question: string) => {
      const text = question.trim();
      if (!text || isLoading) return;
      const url = getChatUrl();

      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const userMessage: PanelMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: text,
        createdAt: new Date().toISOString(),
      };
      const assistantId = crypto.randomUUID();
      let answeredAt: string | null = null;
      setMessages((prev) => [...prev, userMessage]);
      setFailure(null);
      setFailedQuestion(null);
      setIsLoading(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const fail = (kind: PanelFailure, reason: string | null = null) => {
        setMessages((prev) => prev.filter((m) => m.id !== userMessage.id && m.id !== assistantId));
        setFailure({ kind, reason });
        setFailedQuestion(text);
      };

      try {
        if (!url) throw new Error("Chat function URL not configured");
        if (!chatIdRef.current) {
          const chatId = crypto.randomUUID();
          await chatStoreClient.createSession(chatId, idClient, {
            title: text.slice(0, TITLE_LENGTH),
            country,
            source: "dashboard_panel",
          });
          // A cancel or a dashboard switch during the create ends this question,
          // so its chat must not become the conversation's chat.
          if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
          chatIdRef.current = chatId;
          setChatId(chatId);
        }

        const res = await postWithAuthRetry(
          url,
          {
            user_id: userId,
            chat_id: chatIdRef.current,
            user_message: text,
            conversation_history: history,
            id_client: idClient,
            country,
            dashboard: { key: dashboard, view, filters },
          },
          controller.signal
        );

        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as {
            reason?: string;
          } | null;
          const { code } = readProblem(body, "");
          if (res.status === 400 && code === "panel_view_blocked" && body?.reason) {
            fail("blocked", body.reason);
            return;
          }
          fail(res.status === 403 && code === "module_scope_denied" ? "denied" : "failed");
          return;
        }

        const result = await readAnswer(res, (answer) => {
          answeredAt ??= new Date().toISOString();
          const createdAt = answeredAt;
          setMessages((prev) => [
            ...prev.filter((m) => m.id !== assistantId),
            { id: assistantId, role: "assistant", createdAt, ...answer },
          ]);
        });
        if (result.outcome === "failed") {
          fail("failed");
        } else if (result.storedId) {
          const { storedId } = result;
          setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, storedId } : m)));
        }
      } catch (err) {
        // A cancelled question leaves no trace in the conversation.
        if (controller.signal.aborted) {
          setMessages((prev) =>
            prev.filter((m) => m.id !== userMessage.id && m.id !== assistantId)
          );
          return;
        }
        console.error("[QuestionsPanel] question failed", err instanceof Error ? err.message : err);
        fail("failed");
      } finally {
        // A cancelled question already cleared these, and a newer one owns them now.
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsLoading(false);
        }
      }
    },
    [isLoading, messages, dashboard, view, userId, idClient, country, filters]
  );

  const retry = useCallback(() => {
    if (failedQuestion) void ask(failedQuestion);
  }, [ask, failedQuestion]);

  return {
    messages,
    chatId,
    isLoading,
    failure: failure?.kind ?? null,
    failedQuestion,
    // The server's reason when it refused the last question for its view.
    blockedReason: failure?.kind === "blocked" ? failure.reason : null,
    ask,
    retry,
    cancel,
  };
}
